import { fingerprintInit } from '@by-device/fingerprint';
import { sha256 } from 'js-sha256';

import { KEY_VERSION } from '@/constants/api-source-types';
import { API2_HOST } from '@/utils/constants';

import fetchData from './http';
import request from './request';
import { IBaseResponse, IResult } from './types/common';
import {
  IBindGoogle2fa,
  IChangeEmailParams,
  IChangeMobile,
  IChangeWithDrawParams,
  ICountryListItem,
  IEmailOperationType,
  IGetSecurityItemsResult,
  IPreSelfUnbindParams,
  IReportFileUploadParams,
  ISelfUnbindSecurityItemV2,
  ISelfUnbindSecurityRequest,
  ISelfUnbindSecurityResult,
  ISubmitSelfUnbindInfoRequest,
  ITrustDeviceResult,
  IUnbindEmailParams,
} from './types/security';
import { AccountActivityParams } from './types/security';

// temp
export function getUserProfile() {
  const res = request.get<IBaseResponse<any>>(
    `${API2_HOST}/v2/private/user/profile`,
    {
      useCache: true,
    },
  );

  return res;
}

/**
 *
 * @returns 用户信息拆分的安全中心依赖的字段
 */
export function getUserSecurityProfile() {
  return request.get<IBaseResponse<any>>(
    `${API2_HOST}/user/private/profile-v2`,
    {
      useCache: true,
    },
  );
}

// 绑定谷歌验证码。
export function bind(params: IBindGoogle2fa) {
  return fetchData.post(`${API2_HOST}/google2fa/bind`, params);
}

// 可信设备列表
export function trustedDeviceList() {
  const url = `${API2_HOST}/v3/private/user/member/trusted-device-list`;
  return request.get<IBaseResponse<ITrustDeviceResult | null>>(url, {
    useCache: true,
  });
}

// 获取kycinfo
export function getKYCInfo() {
  const url = `${API2_HOST}/v3/private/kyc/kyc-info`;
  return request.post<IBaseResponse<any>>(url, {
    body: JSON.stringify({
      obtain_kyc_token: false,
      obtain_current: true,
      obtain_quotas: false,
      obtain_state: false,
    }),
  });
}

// 修改用户名
export function changeUsername(username: string, subMemberId = '') {
  const url = `${API2_HOST}/user/change-username`;
  const params: {
    username: string;
    sub_member_id?: string;
  } = { username };
  if (subMemberId) {
    params.sub_member_id = subMemberId;
  }
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<any>>(url, {
        body: JSON.stringify(params),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        console.log(error, 'error0000');
        reject(error.response ? error.response.data : error);
      });
  });
}

// 修改密码
export function changePassword(params: { [key: string]: string }) {
  const url = `${API2_HOST}/user/change/password`;
  const newParams: any = {
    risk_token: params.risk_token,
    password: sha256(params.password),
    samepassword: sha256(params.samepassword),
  };
  if (params.old_password) {
    newParams.old_password = sha256(params.old_password);
  }
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify(newParams),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 获取手机号国家列表
export function getAllListCountry() {
  const url = `${API2_HOST}/v2/public/country-list`;
  return new Promise((resolve, reject) => {
    fetchData
      .get<IBaseResponse<ICountryListItem[]>>(url)
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

export function getRegisterCountryCode() {
  const url = `${API2_HOST}/register/countryCode`;
  return new Promise((resolve, reject) => {
    fetchData
      .get<IBaseResponse<ICountryListItem[]>>(url)
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 母账号绑定2fa
export function bind2fa(code: string, secret: string, from: number) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/google2fa/bind`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({
          code, // 谷歌验证码
          secret,
          from,
        }),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 绑定子账号2fa
export function bindSubAccount2fa(
  auth_context: Array<{ [key: string]: any }>,
  google2faCode: string,
  google2faSecret: string,
  from: number,
  bizToken: string,
) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/v3/private/user/member/bind-google2fa`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({
          auth_context,
          google2faCode,
          google2faSecret,
          from,
          bizToken,
        }),
      })
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 绑定手机号
export function bindMobile(
  country_code: string,
  mobile: string,
  type: string,
  verification_code: string,
  from: number,
) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/bind/mobile`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({
          country_code, // 国家代码
          mobile, // 手机
          type, // 类型
          verification_code, // 验证码
          from,
        }),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 更换手机号
export function changeMobile(
  change_mobile: string,
  country_code: string,
  verification_code: string,
  type: string,
  from: number,
) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/change/mobile`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({
          change_mobile, // 手机
          country_code, // 国家代码
          verification_code, // 手机验证码
          type,
          from,
        }),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}
// 获取账户活动列表
export function getAccountActivityList(params: AccountActivityParams) {
  return fetchData.get(`${API2_HOST}/operation-record/list`, {
    body: JSON.stringify(params),
  });
}

// 用户绑定邮箱
export function bindEmail(
  email: string,
  type: string,
  verification_code: string,
  from: number,
) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/bind/email`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({
          email, // 邮箱
          type, // 类型
          verification_code, // 邮箱验证码
          from,
        }),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 提币地址状态修改
export function changeWithdrawAddrLevel(params: IChangeWithDrawParams) {
  const url = `${API2_HOST}/user/private/change/withdraw-addr-level`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<any>>(url, { body: JSON.stringify(params) })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}
// 根据类型获取账户活动列表
export function getAccountActivityListByType(params: AccountActivityParams) {
  return fetchData.get(
    `${API2_HOST}/user/private/operation-record-list-by-type`,
    {
      body: JSON.stringify(params),
    },
  );
}

// 登录状态下禁用账户
export function loginBan(params: any) {
  const url = `${API2_HOST}/user/private/self-ban/enable`;
  return fetchData.post(url, { body: JSON.stringify(params) });
}

// 未登录状态下禁用账户
export function notLoginBan(params: any) {
  const url = `${API2_HOST}/user/public/self-ban-no-login/enable`;
  return fetchData.post(url, { body: JSON.stringify(params) });
}

// 头像接口--判断/获取用户头像--有值说明已配置，空值说明未配置
export function getAvatarInfo() {
  const url = `${API2_HOST}/user/private/member/info`;
  return request.get(url, { useCache: true });
}

// 头像接口--获取用户默认头像列表
export function getDefaultAvatarList() {
  const url = `${API2_HOST}/user/private/avatar/list`;
  return fetchData.get(url);
}

// 头像接口--获取用户nft头像列表
export function getNFTAvatarList(params: { page: number; page_size: number }) {
  const url = `${API2_HOST}/user/private/nft-avatar/list`;
  return fetchData.get(url, { body: JSON.stringify(params) });
}

// 头像接口--设置用户头像
export function updateAvatar(params: {
  avatar_id: number | string;
  type: string;
}) {
  const url = `${API2_HOST}/user/private/update/avatar`;
  return fetchData.post(url, {
    body: JSON.stringify(params),
  });
}

// 登录状态下获取可重置安全项列表
export function loginSecurityItemList() {
  const url = `${API2_HOST}/user/private/self-unbind/user-security-items`;
  return fetchData.get<IBaseResponse<IGetSecurityItemsResult>>(url);
}

// 非登录状态下获取可重置安全项列表
export function notLoginSecurityItemList(params: any) {
  const url = `${API2_HOST}/user/public/self-unbind/user-security-items`;
  return fetchData.post<IBaseResponse<IGetSecurityItemsResult>>(url, {
    body: JSON.stringify(params),
  });
}

// 自主解绑接口
export function selfUnbindSecurity(params: ISelfUnbindSecurityRequest) {
  const url = `${API2_HOST}/user/public/self-unbind/self-unbind-security-item`;
  return fetchData.post<IBaseResponse<ISelfUnbindSecurityResult>>(url, {
    body: JSON.stringify(params),
  });
}

// 未登录下发送邮箱验证码
export function sendEmailNoLogin(params: any) {
  const url = `${API2_HOST}/user/public/send/new/email`;
  return fetchData.post(url, { body: JSON.stringify(params) });
}

// 未登录状态下发送手机验证码
export function sendSmsNoLogin(params: any) {
  const url = `${API2_HOST}/user/public/send/new/mobile`;
  return fetchData.post(url, { body: JSON.stringify(params) });
}

// 安全项解绑提交材料接口
export function submitSelfUnBindInfo(
  params: ISubmitSelfUnbindInfoRequest | any,
) {
  const url = `${API2_HOST}/user/public/self-unbind/submit-self-unbind-info`;
  return fetchData.post(url, { body: JSON.stringify(params) });
}

// 检查邮箱是否可以进行换解绑操作
export function emailOperation(params: IEmailOperationType) {
  const url = `${API2_HOST}/user/private/email/operation`;
  return fetchData.get<any>(url, {
    body: JSON.stringify(params),
  });
}

export function sendEmailPrivate(params: any) {
  return new Promise((resolve, reject) => {
    const url = `${API2_HOST}/user/private/send/email`;
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify(params),
      })
      .then((response) => {
        resolve(response.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 发送短信验证码 有行为验证
export async function sendCodeMobile(params: { [key: string]: any }) {
  await fingerprintInit();
  const getTxId = window?.byDeviceTx?.getTxId;
  const tx_id = await getTxId?.();
  params.key_version = KEY_VERSION;
  const url = `${API2_HOST}/sendcode/mobile`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify(params),
        headers: {
          'tx-id': tx_id,
        },
      })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 解绑邮箱接口
export function unbindEmail(params: IUnbindEmailParams) {
  const url = `${API2_HOST}/user/private/unbind/email`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify(params),
      })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 检查新邮箱是否已经被注册
export function checkEmailExist(email: string) {
  const url = `${API2_HOST}/user/private/email/exist`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({ email }),
      })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 换绑邮箱
export function changeEmail(params: IChangeEmailParams) {
  const url = `${API2_HOST}/user/private/change/email`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify(params),
      })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 自主禁用账户非登录态下获取用户信息
export function getUserInfoByEmailVerifyToken(email_verify_token: string) {
  const url = `${API2_HOST}/user/public/self-ban-no-login/getUserInfoByEmailVerifyToken`;
  return fetchData.get<IBaseResponse<IResult>>(url, {
    body: JSON.stringify({ email_verify_token }),
  });
}

// 检查邮箱是否可以进行换解绑操作
export function mobileOperation(params: IEmailOperationType) {
  const url = `${API2_HOST}/user/private/mobile/operation`;
  return fetchData.get<any>(url, {
    body: JSON.stringify(params),
  });
}

// 检查新手机号是否已经被注册
export function checkMobileExist(mobile: string, country_code: string) {
  const url = `${API2_HOST}/user/private/mobile/exist`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, {
        body: JSON.stringify({ mobile, country_code }),
      })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

// 换绑手机号接口
export function changeMobileNew(params: IChangeMobile) {
  const url = `${API2_HOST}/user/private/change/mobile`;
  return new Promise((resolve, reject) => {
    fetchData
      .post<IBaseResponse<IResult>>(url, { body: JSON.stringify(params) })
      .then((res) => {
        resolve(res.result);
      })
      .catch((error) => {
        reject(error.response ? error.response.data : error);
      });
  });
}

interface IStrategy {
  sence: string | number;
  state?: string;
  ext_info?: string | { [key: string]: any };
  ext_info_str?: string | { [key: string]: any };
}

export function getRiskStrategyApi(params: IStrategy) {
  const url = `${API2_HOST}/user/public/risk/default-intercept`;
  return fetchData.post(url, {
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'x-www-form-urlencoded;charset=UTF-8',
    },
  });
}

/* 安全项找回二期--start */
export function preSelfUnbindInfo(params: IPreSelfUnbindParams) {
  const url = `${API2_HOST}/user/public/self-unbind/pre-submit-self-unbind-info`;
  return fetchData.post<IBaseResponse<IResult>>(url, {
    body: JSON.stringify(params),
  });
}

export function selfUnbindInfoV2(params: any) {
  const url = `${API2_HOST}/user/public/self-unbind/submit-self-unbind-info-v2`;
  return fetchData.post<IBaseResponse<IResult>>(url, {
    body: JSON.stringify(params),
  });
}

export function selfUnbindSecurityItemV2(params: ISelfUnbindSecurityItemV2) {
  const url = `${API2_HOST}/user/public/self-unbind/self-unbind-security-item-v2`;
  return fetchData.post<IBaseResponse<IResult>>(url, {
    body: JSON.stringify(params),
  });
}

// 获取上传接口签名
export function getUploadSign(params: any) {
  const url = `${API2_HOST}/user/public/self-unbind/get-file-upload-sign`;
  return fetchData.post<IBaseResponse<IResult>>(url, {
    body: JSON.stringify(params),
  });
}

// 上报文件上传aws完成
export function reportFileUpload(params: IReportFileUploadParams) {
  const url = `${API2_HOST}/user/public/self-unbind/report-file-upload`;
  return fetchData.post<IBaseResponse<IResult>>(url, {
    body: JSON.stringify(params),
  });
}

// 设置提币免验证
export function setWithdrawAddressLevel(params: {
  status: '1' | '2';
  risk_token?: string;
}) {
  const url = `${API2_HOST}/user/private/change/address-super-verification`;
  return fetchData.post<{
    ret_code: number;
    ret_msg: string;
    result: { op_result: 0 | 1; risk_token: string; risk_token_type: string };
  }>(url, {
    body: JSON.stringify(params),
  });
}
